import React, { useState, useEffect } from 'react';
import { BrowserRouter as Router, Routes, Route } from 'react-router-dom';
import { MsalProvider, useMsal, useIsAuthenticated } from '@azure/msal-react';
import { msalInstance } from './lib/supabase';
import { Layout } from './components/Layout/Layout';
import { MaquinariasList } from './pages/Maquinarias/MaquinariaList';
import { MaquinariaDetail } from './pages/Maquinarias/MaquinariaDetail';
import { MaquinariaForm } from './pages/Maquinarias/MaquinariaForm';
import { ReportesList } from './pages/Reportes/ReportesList';
import { UserManagement } from './pages/Admin/UserManagement';
import { OperatorManagement } from './pages/Admin/OperatorManagement';
import { QRVerification } from './pages/Maquinarias/QRVerification';
import { DefaultRedirect } from './hooks/DefaultRedirect';
import { Building2, AlertCircle, Shield, Mail, LogIn, Users } from 'lucide-react';
import { loginRequest } from './config/auth';

function AuthenticatedApp() {
  return (
    <Layout>
      <Routes>
        <Route path="/" element={<DefaultRedirect />} />
        <Route path="/maquinarias" element={<MaquinariasList />} />
        <Route path="/maquinarias/nueva" element={<MaquinariaForm />} />
        <Route path="/maquinarias/:id" element={<MaquinariaDetail />} />
        <Route path="/reportes" element={<ReportesList />} />
        <Route path="/admin/users" element={<UserManagement />} />
        <Route path="/admin/operators" element={<OperatorManagement />} />
      </Routes>
    </Layout>
  );
}

function Login() {
  const { instance } = useMsal();
  const [loginLoading, setLoginLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleLogin = async () => {
    console.log('Iniciando proceso de login manual...');
    setLoginLoading(true);
    setError(null);
    try {
      const loginResponse = await instance.loginPopup(loginRequest);
      console.log('Login exitoso:', loginResponse);
      // Guardar flag para indicar que el usuario se autenticó manualmente
      sessionStorage.setItem('manual_login', 'true');
    } catch (error: any) {
      console.error('Error durante login:', error);
      
      if (error.errorCode === 'popup_closed_by_user') {
        setError('El proceso de inicio de sesión fue cancelado.');
      } else if (error.errorCode === 'consent_required') {
        setError('Se requiere consentimiento adicional. Por favor contacte al administrador.');
      } else if (error.errorCode === 'interaction_in_progress') {
        setError('Ya hay un proceso de autenticación en curso.');
      } else {
        setError('Error al iniciar sesión. Por favor intente nuevamente.');
      }
    } finally {
      setLoginLoading(false);
    }
  };

  return (
    <div style={{
      minHeight: '100vh',
      backgroundImage: 'url("/Background.jpg")',
      backgroundSize: 'cover',
      backgroundPosition: 'center',
      display: 'flex', 
      flexDirection: 'column',
      alignItems: 'center',
      justifyContent: 'center',
      padding: '1rem',
      fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif'
    }}>

      {/* Logo EMIN */}
      <div style={{
        position: 'absolute',
        top: '24px',
        right: '24px',
        zIndex: 1000
      }}>
        <img
          src="/Logo-Grupo-Emin.png" 
          alt="Logo EMIN"
          style={{
            width: '80px',
            height: 'auto',
            maxWidth: '100%',
            objectFit: 'contain'
          }}
        />
      </div>
      
      {/* Main Container */}
      <div style={{
        width: '100%',
        maxWidth: '420px',
        backgroundColor: 'white',
        borderRadius: '12px',
        boxShadow: '0 4px 6px rgba(0, 0, 0, 0.05), 0 10px 15px rgba(0, 0, 0, 0.1)',
        border: '1px solid rgba(0, 74, 152, 0.1)',
        overflow: 'hidden',
        margin:'70px 0'
      }}>
        
        {/* Header */}
        <div style={{
          padding: '2rem 2.5rem 1.5rem 2.5rem',
          textAlign: 'center',
          backgroundColor: 'white',
          borderBottom: '1px solid #e5e7eb'
        }}>
          <div style={{
            display: 'flex',
            justifyContent: 'center'
          }}>
            <div style={{
              display: 'flex',
              alignItems: 'center',
              gap: '1rem'
            }}>
              <div style={{
                padding: '0.625rem',
                backgroundColor: '#fcc43c',
                borderRadius: '8px',
                boxShadow: '0 4px 6px rgba(0, 74, 152, 0.2)'
              }}>
                <Building2 style={{
                  height: '1.5rem',
                  width: '1.5rem',
                  color: 'white',
                  strokeWidth: 1.5
                }} />
              </div>
              <div style={{
                textAlign: 'left'
              }}>
                <h1 style={{
                  fontSize: '1.5rem',
                  fontWeight: '700',
                  color: '#1f2937',
                  margin: 0,
                  letterSpacing: '-0.025em',
                  lineHeight: '1.2'
                }}>
                  CAEMIN
                </h1>
                <div style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '0.5rem',
                  marginTop: '0.25rem'
                }}>
                  <div style={{
                    width: '0.375rem',
                    height: '0.375rem',
                    backgroundColor: '#10b981',
                    borderRadius: '50%'
                  }}></div>
                  <span style={{
                    fontSize: '0.8125rem',
                    color: '#6b7280',
                    fontWeight: '500',
                    textTransform: 'uppercase',
                    letterSpacing: '0.05em'
                  }}>
                    Sistema de Gestión de Maquinaria
                  </span>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Form Section */}
        <div style={{ padding: '2rem 2.5rem' }}>
          <div style={{
            marginBottom: '1.5rem'
          }}>
            <h2 style={{
              fontSize: '1.25rem',
              fontWeight: '600',
              color: '#1f2937',
              marginBottom: '0.5rem',
              letterSpacing: '-0.025em',
              textAlign: 'center'
            }}>
              Acceso Corporativo
            </h2>
            <p style={{
              color: '#6b7280',
              fontSize: '0.9375rem',
              margin: 0,
              lineHeight: '1.5',
              textAlign: 'center'
            }}>
              Use sus credenciales corporativas de Microsoft para acceder al sistema
            </p>
          </div>

          <div style={{ display: 'flex', flexDirection: 'column', gap: '1.25rem' }}>
            
            {/* Error Message */}
            {error && (
              <div style={{
                backgroundColor: '#fef2f2',
                border: '1px solid #fecaca',
                borderRadius: '8px',
                padding: '1rem'
              }}>
                <div style={{ display: 'flex', alignItems: 'start' }}>
                  <AlertCircle style={{
                    height: '1.125rem',
                    width: '1.125rem',
                    color: '#dc2626',
                    marginRight: '0.75rem',
                    marginTop: '0.125rem',
                    flexShrink: 0,
                    strokeWidth: 1.5
                  }} />
                  <div style={{ width: '100%' }}>
                    <p style={{
                      fontSize: '0.875rem',
                      color: '#b91c1c',
                      margin: '0',
                      fontWeight: '500'
                    }}>
                      {error}
                    </p>
                  </div>
                </div>
              </div>
            )}

            {/* Microsoft Login Button */}
            <button
              onClick={handleLogin}
              disabled={loginLoading}
              style={{
                width: '100%',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                gap: '0.75rem',
                backgroundColor: loginLoading ? '#9ca3af' : '#0078d4',
                color: 'white',
                paddingTop: '1rem',
                paddingBottom: '1rem',
                paddingLeft: '1.5rem',
                paddingRight: '1.5rem',
                borderRadius: '8px',
                fontSize: '0.9375rem',
                fontWeight: '600',
                border: 'none',
                cursor: loginLoading ? 'not-allowed' : 'pointer',
                transition: 'all 0.2s ease',
                letterSpacing: '-0.025em',
                boxShadow: '0 1px 2px rgba(0, 0, 0, 0.05)'
              }}
              onMouseOver={(e) => {
                if (!loginLoading) {
                  e.currentTarget.style.backgroundColor = '#106ebe';
                  e.currentTarget.style.boxShadow = '0 4px 6px rgba(0, 0, 0, 0.1)';
                  e.currentTarget.style.transform = 'translateY(-1px)';
                }
              }}
              onMouseOut={(e) => {
                if (!loginLoading) {
                  e.currentTarget.style.backgroundColor = '#0078d4';
                  e.currentTarget.style.boxShadow = '0 1px 2px rgba(0, 0, 0, 0.05)';
                  e.currentTarget.style.transform = 'translateY(0)';
                }
              }}
            >
              {loginLoading ? (
                <>
                  <div style={{
                    width: '1.125rem',
                    height: '1.125rem',
                    border: '2px solid transparent',
                    borderTop: '2px solid white',
                    borderRadius: '50%',
                    animation: 'spin 1s linear infinite'
                  }}></div>
                  <span>Iniciando sesión...</span>
                </>
              ) : (
                <>
                  <LogIn style={{
                    width: '1.125rem',
                    height: '1.125rem',
                    strokeWidth: 1.5
                  }} />
                  <span>Iniciar sesión con Microsoft</span>
                </>
              )}
            </button>

            {/* Info sobre acceso */}
            <div style={{
              backgroundColor: '#f0f9ff',
              border: '1px solid #bae6fd',
              borderRadius: '8px',
              padding: '1rem'
            }}>
              <div style={{ display: 'flex', alignItems: 'start' }}>
                <Users style={{
                  height: '1.125rem',
                  width: '1.125rem',
                  color: '#0284c7',
                  marginTop: '0.125rem',
                  marginRight: '0.75rem',
                  flexShrink: 0,
                  strokeWidth: 1.5
                }} />
                <div>
                  <h4 style={{
                    fontSize: '0.875rem',
                    fontWeight: '600',
                    color: '#075985',
                    margin: '0 0 0.5rem 0'
                  }}>
                    Sistema de Gestión
                  </h4>
                  <p style={{
                    fontSize: '0.75rem',
                    color: '#0c4a6e',
                    margin: '0',
                    lineHeight: '1.4'
                  }}>
                    Administre y controle el estado de la maquinaria, 
                    documentación y mantenimientos de forma centralizada.
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Footer */}
        <div style={{
          padding: '1.5rem 2.5rem 2rem 2.5rem',
          borderTop: '1px solid rgba(0, 74, 152, 0.1)',
          backgroundColor: 'rgba(248, 250, 252, 0.5)'
        }}>
          <div style={{ textAlign: 'center' }}>
            <p style={{
              fontSize: '0.875rem',
              color: '#6b7280',
              marginBottom: '0.75rem',
              margin: '0 0 0.75rem 0',
              fontWeight: '500'
            }}>
              ¿Necesita asistencia técnica?
            </p>
            <div style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              gap: '0.5rem',
              marginBottom: '1rem'
            }}>
              <Mail style={{
                height: '1rem',
                width: '1rem',
                color: '#004A98',
                strokeWidth: 1.5
              }} />
              <a
                href="mailto:ticket@emin.cl"
                style={{
                  fontSize: '0.875rem',
                  color: '#004A98',
                  textDecoration: 'none',
                  fontWeight: '600',
                  transition: 'color 0.2s ease'
                }}
                onMouseOver={(e) => {
                  e.currentTarget.style.color = '#003875';
                }}
                onMouseOut={(e) => {
                  e.currentTarget.style.color = '#004A98';
                }}
              >
                ticket@emin.cl
              </a>
            </div>

            <div style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              gap: '2rem',
              fontSize: '0.8125rem',
              color: '#9ca3af',
              marginBottom: '1rem'
            }}>
              <div style={{
                display: 'flex',
                alignItems: 'center',
                gap: '0.375rem'
              }}>
                <Shield style={{
                  height: '0.875rem',
                  width: '0.875rem',
                  color: '#10b981',
                  strokeWidth: 1.5
                }} />
                <span style={{ fontWeight: '500' }}>Conexión SSL Segura</span>
              </div>
              <div style={{
                display: 'flex',
                alignItems: 'center',
                gap: '0.375rem'
              }}>
                <div style={{
                  width: '0.5rem',
                  height: '0.5rem',
                  backgroundColor: '#10b981',
                  borderRadius: '50%',
                  marginTop: '0.05rem',
                  animation: 'pulse 2s cubic-bezier(0.4, 0, 0.6, 1) infinite'
                }}></div>
                <span style={{ fontWeight: '500' }}>Sistema Operativo</span>
              </div>
            </div>

            <p style={{
              fontSize: '0.8125rem',
              color: '#9ca3af',
              margin: 0,
              fontWeight: '400'
            }}>
              © 2025 Grupo EMIN. Todos los derechos reservados.
            </p>
          </div>
        </div>
      </div>

      {/* Footer logos empresas */}
      <div style={{
        width: '100%',
        display: 'flex',
        flexWrap: 'wrap',
        justifyContent: 'center',
        alignItems: 'center',
        gap: '24px',
        padding: '1rem',
      }}>
        <img src="/logos-empresas/logo-emin-ic.png" alt="EMIN" style={{ height: '25px', objectFit: 'contain' }} />
        <img src="/logos-empresas/logo-thm.png" alt="THM" style={{ height: '25px', objectFit: 'contain' }} />
        <img src="/logos-empresas/logo-morpet.png" alt="MORPET" style={{ height: '25px', objectFit: 'contain' }} />
        <img src="/logos-empresas/logo-caemin.png" alt="CAEMIN" style={{ height: '25px', objectFit: 'contain' }} />
        <img src="/logos-empresas/logo-emin-sistemas.png" alt="EMIN Sistemas" style={{ height: '25px', objectFit: 'contain' }} />
        <img src="/logos-empresas/logo-emin-geoestructuras.png" alt="EMIN Geoestructuras" style={{ height: '25px', objectFit: 'contain' }} />
        <img src="/logos-empresas/logo-epav.png" alt="EPAV" style={{ height: '25px', objectFit: 'contain' }} />
        <img src="/logos-empresas/logo-beka.png" alt="BEKA" style={{ height: '25px', objectFit: 'contain' }} />
        <img src="/logos-empresas/logo-agua.png" alt="Agua del Desierto" style={{ height: '25px', objectFit: 'contain' }} />
      </div>

      {/* CSS Animations */}
      <style>{`
        @keyframes spin {
          from { transform: rotate(0deg); }
          to { transform: rotate(360deg); }
        }
        @keyframes pulse {
          0%, 100% { opacity: 1; }
          50% { opacity: 0.5; }
        }
      `}</style>
    </div>
  );
}

function AppContent() {
  const isAuthenticated = useIsAuthenticated();
  const { accounts } = useMsal();
  const [isReady, setIsReady] = useState(false);

  useEffect(() => {
    // Verificar si hay un flag de logout
    const logoutPending = sessionStorage.getItem('logout_pending');
    if (logoutPending) {
      sessionStorage.removeItem('logout_pending');
      sessionStorage.removeItem('manual_login');
      console.log('Logout detectado, mostrando login...');
    }

    // Pequeño delay para asegurar que todo está listo
    const timer = setTimeout(() => {
      setIsReady(true);
    }, 100);

    return () => clearTimeout(timer);
  }, []);

  // No mostrar nada hasta que esté listo
  if (!isReady) {
    return null;
  }

  // Verificar autenticación y mostrar el componente apropiado
  if (isAuthenticated && accounts.length > 0) {
    console.log('Usuario autenticado, mostrando app...');
    return (
      <Routes>
        <Route path="/maquinarias/:id/verify" element={<QRVerification />} />
        <Route path="/*" element={<AuthenticatedApp />} />
      </Routes>
    );
  } else {
    console.log('Usuario no autenticado, mostrando login...');
    return (
      <Routes>
        <Route path="/maquinarias/:id/verify" element={<QRVerification />} />
        <Route path="/*" element={<Login />} />
      </Routes>
    );
  }
}

function App() {
  // Limpiar cualquier intento de autenticación automática al inicio
  useEffect(() => {
    const handleLoad = () => {
      const logoutFlag = sessionStorage.getItem('logout_pending');
      if (logoutFlag) {
        console.log('Limpiando flag de logout...');
        sessionStorage.removeItem('logout_pending');
      }
    };

    handleLoad();
  }, []);

  return (
    <MsalProvider instance={msalInstance}>
      <Router>
        <AppContent />
      </Router>
    </MsalProvider>
  );
}

export default App;